import React, { useEffect, useState } from "react";
import Dialog from "../Dialog/Dialog";
import MultiSelectAutocomplete from "../MultiSelectAutocomplete/MultiSelectAutocomplete";

export default function ColumnsDialog({
  open,
  onClose,
  columns,
  setColumns,
  attributes,
  title
}) {
  const [currentColumn, setCurrentColumn] = useState("");

  // =========================
  // ORDER ENFORCER (IMPORTANT)
  // =========================
  const sortColumns = (cols) => {
    const map = new Map(cols.map((c) => [c.key, c]));

    return attributes
      .map((attr) => map.get(attr.value))
      .filter(Boolean);
  };

  const defaultColumns = attributes
    .filter((a) => a.is_default === "true")
    .map((a) => ({
      key: a.value,
      label: a.label
    }));

  const [selectedColumns, setSelectedColumns] = useState(() =>
    sortColumns(columns || [])
  );

  const isHiddenDefault = (col) => col?.as_default_col === "hidden";

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  // =========================
  // ADD COLUMN
  // =========================
  const addColumn = (value) => {
    const columnValue = value || currentColumn;
    if (!columnValue) return;

    if (selectedColumns.find((c) => c.key === columnValue)) return;

    const defaultCol = defaultColumns.find((d) => d.key === columnValue);
    const attr = attributes.find((a) => a.value === columnValue);

    const newCol = {
      ...(defaultCol || {}),
      key: columnValue,
      label: defaultCol?.label || attr?.label || columnValue
    };

    const newCols = sortColumns([...selectedColumns, newCol]);

    setSelectedColumns(newCols);
    setColumns(newCols);
    localStorage.setItem(`columns_${title}`, JSON.stringify(newCols));
    setCurrentColumn("");
  };

  // =========================
  // REMOVE COLUMN
  // =========================
  const removeColumn = (colKey) => {
    const newCols = sortColumns(
      selectedColumns.filter((c) => c.key !== colKey)
    );

    setSelectedColumns(newCols);
    setColumns(newCols);
    localStorage.setItem(`columns_${title}`, JSON.stringify(newCols));
  };

  // =========================
  // OPTIONS
  // =========================
  const availableOptions = attributes
    .filter((a) => {
      const key = a.value;

      const selected = selectedColumns.some((c) => c.key === key);
      if (selected) return false;

      return a?.is_default !== "hidden";
    })
    .map((a) => a.label);

  const selectedKeys = selectedColumns.map((c) => c.label);
  const defaultLabels = defaultColumns.map((c) => c.label);

  // =========================
  // RENDER
  // =========================
  return (
    <Dialog open={open} onClose={onClose}>
      <h2>Choose Columns</h2>

      <MultiSelectAutocomplete
        mode="showAll"
        options={availableOptions}
        value={selectedKeys}
        nonRemovableItems={defaultLabels}
        placeholder="Add columns..."
        onChange={(newLabels) => {
          const unsorted = newLabels.map((label) => {
            const attr = attributes.find((a) => a.label === label);

            return {
              key: attr?.value || label,
              label
            };
          });

          const newCols = sortColumns(unsorted);

          setSelectedColumns(newCols);
          setColumns(newCols);
          localStorage.setItem(`columns_${title}`, JSON.stringify(newCols));
        }}
      />

      {/* Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 12
        }}
      >
        <button
          className="label-btn"
          style={{ "--label-btn-color": "#43A047" }}
          onClick={() => {
            const resetCols = sortColumns(defaultColumns);

            setSelectedColumns(resetCols);
            setColumns(resetCols);

            localStorage.setItem(
              `columns_${title}`,
              JSON.stringify(resetCols)
            );
          }}
        >
          Reset
        </button>

        <button
          className="label-btn"
          style={{ "--label-btn-color": "#43A047" }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Dialog>
  );
}